import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { DebounceInput } from 'react-debounce-input';
import { v4 as uuidv4 } from 'uuid';
import './selectBox.scss';

import { ReactComponent as ChevronDown } from '../../../assets/chevronDown.svg';
import searchSvg from "../../../assets/magnifier.svg";
import closeSvg from "../../../assets/close.svg";


let defaultDef = {
  "codeField": "code",
  "descField": "desc",
}

class DropDown extends Component {
  defaultName = "dropDown" + uuidv4();
  dropdownRef = React.createRef();
  /**
   * name: name to be given to the filter parameter html element
   * id: id to be given to the filter parameter html element
   * definition: definition of the filter parameter (fields to be used for code and description)
   * data: data to be used for the filter parameter if its type of drop down
   * width: width of the filter parameter html element
   * onSelect: callback function to be called when an option is selected
   * searchText: text to be used for searching or text entered in the search box
   * selectedOption: readonly property displaying currently selected option
   * searchBoxPlaceholder: placeholder text for the search box
   * showSelectionText: boolean flag to show/hide selection text
   * selectionText: text to be displayed when no option is selected
   * dropdownClasses: classes to be applied to the dropdown container, readonly
   * chevClasses: classes to be applied to the dropdown chevron, readonly
   */
  state = {
    name: typeof this.props.name === "string" ? this.props.name : this.defaultName,
    id: typeof this.props.id === "string" ? this.props.id : this.defaultName,
    definition: typeof this.props.definition === "object" ? this.props.definition : defaultDef, 
    data: typeof this.props.data === "object" ? this.props.data : [],
    width: typeof this.props.width === "string" ? this.props.width : "300px",
    onSelect: typeof this.props.onSelect === "function" ? this.props.onSelect : this.onSelect,
    searchText: "",
    selectedOption: typeof this.props.defaultValue === "string" ? this.props.defaultValue : "",
    searchBoxPlaceholder: "Type here to search",
    showSelectionText: typeof this.props.showSelectionText === "boolean" ? this.props.showSelectionText : true,
    selectionText: typeof this.props.selectionText === "string" ? this.props.selectionText : "-- Select --",
    dropdownClasses: ['options-container', 'd-flex', 'flex-column'],
    chevClasses: ['ml-auto']
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)
    ) {
      const { dropdownClasses, chevClasses } = this.state;
      if (dropdownClasses.indexOf('active') !== -1) {
        dropdownClasses.pop();
        chevClasses.pop();
        this.setState({ dropdownClasses, chevClasses });
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Check if the definition prop has changed
    if(prevProps.definition !== this.props.definition){
      // If it has, update the state with the new definition
      this.setState({ definition: this.props.definition });
    }
    // Check if the data prop has changed
    if(prevProps.data !== this.props.data){
      // If it has, update the state with the new data
      this.setOptionsData(this.props.data);
    }
    // Check if the showSelectionText prop has changed
    if(prevProps.showSelectionText !== this.props.showSelectionText){
      // If it has, update the state with the new showSelectionText
      this.setState({ showSelectionText: this.props.showSelectionText });
    }
    // Check if the selectionText prop has changed
    if(prevProps.selectionText !== this.props.selectionText){
      // If it has, update the state with the new selectionText
      this.setState({ selectionText: this.props.selectionText });
    }
    // Check if the name prop has changed
    if(prevProps.name !== this.props.name){
      // If it has, update the state with the new name
      this.setState({ name: this.props.name });
    }
    // Check if the id prop has changed
    if(prevProps.id !== this.props.id){
      // If it has, update the state with the new id
      this.setState({ id: this.props.id });
    }

  }

  setOptionsData = (data) => {
    this.setState({ data: data });
  }

  /** Handle search input changes. 
   * Filter the given data to find the matching data.
   * Update the options displayed to user with the matching values from the data */
  handleSearch = (e) => {
    // Get the search text from the input and filter the data to find matching options
    let filteredOptions = this.props.data.filter((option) => {
      if (!e.target.value || e.target.value === "") return null;
      // Check if the option matches the search text, case insensitive.
      if(!(this.state.definition && this.state.definition.descField && option[this.state.definition.descField] !== undefined)) return null;
      return option[this.state.definition.descField].toLowerCase().includes(e.target.value.toLowerCase())
    });
    // Update the search text and options data in the state
    this.setState({ searchText: e.target.value });
    // Update the options data with the filtered options
    this.setOptionsData(filteredOptions);
  }

  handleSearchClose = (e) => {
    // Clear the search text and reset the options data
    this.setState({ data: this.props.data, searchText: "" });
  }

  onSelect = (e, option) => {
    // Handle the selection of an option. 
    // This is default dummy function in case no function is provided by developer while defining dropdonw definition.
    // console.log("Selected option:", e, option);
  }

  // Handle option click events, this function is invoked when user selects one of the options displayed in dropdown
  onOptionClick = (e, option, ctlId, ctlName) => {
    // Update the selected option in the state
    const { dropdownClasses, chevClasses } = this.state;
    // Remove the active class from the dropdown and chev elements
    dropdownClasses.pop();
    chevClasses.pop();
    // Reset the search text and selected option
    this.setState({ dropdownClasses, chevClasses, searchText: "", selectedOption: option[this.state.definition.descField] });
    // Reset the options data to the original data
    this.setOptionsData(this.props.data);
    // Call the onSelect function if it is defined
    if (typeof this.state.onSelect === "function"){
      // Call the onSelect function with the appropriate arguments
      this.state.onSelect(e, option, ctlId, ctlName);
    }
  }

  // Handle dropdown toggle events
  handleDropdownToggle = (e) => {
    // console.log("Toggled", e);
    const { dropdownClasses, chevClasses } = this.state;
    // console.log("dropdownClasses, index", dropdownClasses, dropdownClasses.indexOf('active'));
    if (dropdownClasses.indexOf('active') === -1) {
      dropdownClasses.push('active')
      chevClasses.push('toggle-chev')
    } else {
      dropdownClasses.pop()
      chevClasses.pop()
    }
    this.setState({ dropdownClasses, chevClasses })

  }

  render() {
    return (
        <div className="select-box" style={{width: this.state.width, minWidth: this.state.width}} ref={this.dropdownRef} >
          <div style={{width: "362px", display: "flex", flexDirection: "column" }} className={this.state.dropdownClasses.join(' ')}>
            <div className="d-flex flex-grow-1 align-items-center search-box-outline position-sticky bg-white" style={{ top: 0, zIndex: 1 }}>
              <img className="search-box-magnifier" src={searchSvg} alt="" srcSet="" />
              <DebounceInput className="search-box" minLength={2} debounceTimeout={300} value={this.state.searchText} onChange={this.handleSearch} placeholder = {this.state.searchBoxPlaceholder} />
              <img className="search-box-close" src={closeSvg} alt="" srcSet="" onClick={this.handleSearchClose} />
            </div>
            {
              this.state.showSelectionText &&
              <Fragment key={`frg-${this.state.name}-defaultSelectionText`}>
                <div className="option" htmlFor={`op-${this.state.id}-defaultCode`} onClick={(e) => this.onOptionClick(e, {}, `op-${this.state.id}-defaultCode`, `${this.state.name}`)} >
                  <input type="checkbox" className="checkbox" id={`op-${this.state.id}-defaultCode`} name={`op-${this.state.name}`} value={this.state.selectionText} />
                  <label style={{ margin: 0 }}>{this.state.selectionText}</label>
                </div>
                <span style={{ borderBottom: '1px solid #e3e2e2' }}></span>
              </Fragment>
            }

            {this.state.data.map((option, index) => {
              let strCode = option[this.state.definition.codeField];
              let strDesc = option[this.state.definition.descField];
              let ctlDivId = `div-${this.state.id}-${strCode}`;
              let ctlDivName = `div-${this.state.name}-${strCode}`;
              let ctlTxtId = `op-${this.state.id}-${strCode}`;
              let ctlTxtName = `op-${this.state.name}`;
              let ctlLblId = `txtLbl-${this.state.id}-${strCode}`;
              let ctlLblName = `txtLbl-${this.state.name}`;
              return (
                  <Fragment key={`frg-${this.state.name}-${strCode}-${index}`}>
                    <div className="option" htmlFor={strCode} onClick={(e) => this.onOptionClick(e, option, ctlTxtId,`${this.state.name}`)} >
                      <input type="checkbox" className="checkbox" id={ctlTxtId} name={ctlTxtName} value={strDesc} />
                      <label id={ctlLblId} name={ctlLblName} style={{ margin: 0 }}>{strDesc}</label>
                    </div>
                    <span style={{ borderBottom: '1px solid #e3e2e2' }}></span>
                  </Fragment>
                );
              }
              )
            }
          </div>
          <div onClick={this.handleDropdownToggle} className="selected d-flex align-items-center" >
            <img src={this.props.icon} alt="" className='icon' />
            <div className={'text-truncate'}>
              <span className={this.state.selectedOption ? 'initText' : 'selectedInitText'}>
                {this.state.selectedOption ? this.state.selectedOption :  this.state.showSelectionText ? this.state.selectionText : null}
              </span>
            </div>
            <ChevronDown className={this.state.chevClasses.join(' ')} width="23px" />
          </div>
        </div>
    )
  }
}

const mapStateToProps = (state) => ({
})

const mapDispatchToProps = (dispatch) => ({
})

export default connect(mapStateToProps, mapDispatchToProps)(DropDown)
